import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import CountUp from 'react-countup';
import toast from 'react-hot-toast';
import { HiOutlinePlusCircle, HiOutlineTrash, HiOutlineChartPie, HiOutlineRefresh } from 'react-icons/hi';
import { Tooltip } from 'react-tooltip';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import TokenStack from './TokenStack';
import { PROTOCOL_COLORS, CHAIN_COLORS } from '../data';
import { useTheme } from '../theme';
import { useIsMobile } from '../useMediaQuery';
import { useWallet, formatAddress } from '../contexts/WalletContext';
import { fetchWalletPositions } from '../walletApi';

const WALLET_CHAINS = ['Ethereum', 'Arbitrum', 'Optimism', 'Base', 'Polygon'];

function fmtUsd(n) {
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(2)}K`;
  return `$${n.toFixed(2)}`;
}

function daysBetween(dateStr) {
  const entered = new Date(dateStr);
  const now = new Date();
  const diff = now - entered;
  return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
}

function fmtFeeTier(feeTier) {
  const pct = feeTier / 10000;
  return `${pct.toFixed(2)}%`;
}

const STORAGE_KEY = 'poolscope_portfolio';

function loadPositions() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
}

const METRIC_TOOLTIPS = {
  INVESTED: 'The original USD amount you deposited into this pool.',
  'CURRENT APR': 'The annualized percentage rate this pool is currently offering.',
  'EST. EARNINGS': 'Estimated yield earned since your entry date, based on current APR.',
  'EST. VALUE': 'Your invested amount plus estimated earnings to date.',
  'DAYS HELD': 'Number of days since your entry date.',
};

const cardVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.97 },
  visible: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, scale: 0.95, y: -10, transition: { duration: 0.2 } },
};

function SkeletonCard({ colors }) {
  return (
    <div style={{
      background: colors.surface,
      borderRadius: 12,
      padding: "20px",
      boxShadow: `0 1px 4px ${colors.shadow}`,
      border: `1px solid ${colors.border}`,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <div style={{ width: 28, height: 28, borderRadius: "50%", background: colors.border }} />
        <div>
          <div style={{ width: 100, height: 14, borderRadius: 4, background: colors.border, marginBottom: 6 }} />
          <div style={{ width: 60, height: 10, borderRadius: 3, background: colors.border }} />
        </div>
      </div>
      {[1, 2, 3].map(i => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
          <div style={{ width: 70, height: 10, borderRadius: 3, background: colors.border }} />
          <div style={{ width: 50, height: 10, borderRadius: 3, background: colors.border }} />
        </div>
      ))}
    </div>
  );
}

export default function PortfolioView({ pools }) {
  const { colors } = useTheme();
  const isMobile = useIsMobile();
  const { account, isConnected, isConnecting, connect, disconnect } = useWallet();
  const [positions, setPositions] = useState(loadPositions);
  const [showForm, setShowForm] = useState(false);

  // Wallet positions state
  const [walletPositions, setWalletPositions] = useState([]);
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletError, setWalletError] = useState(null);
  const [walletProgress, setWalletProgress] = useState('');
  const [includeWalletInTotals, setIncludeWalletInTotals] = useState(true);

  // Persist positions to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(positions));
  }, [positions]);

  // Fetch wallet positions across all chains when connected
  const fetchAllWalletPositions = useCallback(async () => {
    if (!account) return;
    setWalletLoading(true);
    setWalletError(null);
    setWalletProgress('Starting scan...');

    const allPositions = [];
    let hadError = false;

    for (const chain of WALLET_CHAINS) {
      try {
        setWalletProgress(`Scanning ${chain}...`);
        const chainPositions = await fetchWalletPositions(account, chain, (msg) => {
          setWalletProgress(`${chain}: ${msg}`);
        });
        allPositions.push(...chainPositions);
      } catch (err) {
        console.warn(`Failed to fetch positions on ${chain}:`, err.message);
        hadError = true;
      }
    }

    setWalletPositions(allPositions);
    setWalletProgress('');
    setWalletLoading(false);

    if (allPositions.length === 0 && hadError) {
      setWalletError('Some chains failed to load. Try refreshing.');
    }
  }, [account]);

  useEffect(() => {
    if (!isConnected || !account) {
      setWalletPositions([]);
      setWalletError(null);
      return;
    }
    fetchAllWalletPositions();
  }, [account, isConnected, fetchAllWalletPositions]);

  const handleConnectWallet = useCallback(async () => {
    try {
      await connect();
    } catch (err) {
      toast.error(err.message || 'Failed to connect wallet');
    }
  }, [connect]);

  // Cross-reference wallet positions with our pools to find matching APR
  const enrichedWalletPositions = useMemo(() => {
    return walletPositions.map(wp => {
      const pairName = `${wp.token0.symbol}/${wp.token1.symbol}`;
      const reverseName = `${wp.token1.symbol}/${wp.token0.symbol}`;
      const matchingPool = pools.find(p =>
        p.chain === wp.chain &&
        (p.name === pairName || p.name === reverseName) &&
        p.protocol === 'Uniswap V3'
      );
      return { ...wp, pairName, matchingPool };
    });
  }, [walletPositions, pools]);

  // Wallet summary
  const walletSummary = useMemo(() => {
    if (enrichedWalletPositions.length === 0) {
      return { totalValue: 0, totalFees: 0, positionCount: 0, chainsActive: 0 };
    }
    const totalValue = enrichedWalletPositions.reduce((s, p) => s + p.totalValueUsd, 0);
    const totalFees = enrichedWalletPositions.reduce((s, p) => s + p.feesUsd, 0);
    const chains = new Set(enrichedWalletPositions.map(p => p.chain));
    return { totalValue, totalFees, positionCount: enrichedWalletPositions.length, chainsActive: chains.size };
  }, [enrichedWalletPositions]);

  const [selectedPoolId, setSelectedPoolId] = useState('');
  const [amount, setAmount] = useState('');
  const [entryDate, setEntryDate] = useState(new Date().toISOString().split('T')[0]);
  const [poolSearch, setPoolSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [sortBy, setSortBy] = useState('earnings');

  const filteredPools = useMemo(() => {
    if (!poolSearch) return pools.slice(0, 20);
    const q = poolSearch.toLowerCase();
    return pools.filter(p =>
      p.name.toLowerCase().includes(q) ||
      p.tokens.some(t => t.toLowerCase().includes(q)) ||
      p.protocol.toLowerCase().includes(q) ||
      p.chain.toLowerCase().includes(q)
    ).slice(0, 20);
  }, [pools, poolSearch]);

  const selectedPool = pools.find(p => String(p.id) === String(selectedPoolId));

  const handleAdd = () => {
    if (!selectedPool || !amount || Number(amount) <= 0) {
      toast.error('Please select a pool and enter a valid amount.');
      return;
    }
    const newPosition = {
      id: Date.now(),
      poolId: selectedPool.id,
      poolName: selectedPool.name,
      protocol: selectedPool.protocol,
      chain: selectedPool.chain,
      tokens: selectedPool.tokens,
      amountInvested: Number(amount),
      entryDate: entryDate || new Date().toISOString().split('T')[0],
    };
    setPositions(prev => [...prev, newPosition]);
    setSelectedPoolId('');
    setAmount('');
    setPoolSearch('');
    setEntryDate(new Date().toISOString().split('T')[0]);
    toast.success('Position added!');
  };

  const handleDelete = (posId) => {
    setPositions(prev => prev.filter(p => p.id !== posId));
    toast('Position removed', { icon: '\uD83D\uDDD1\uFE0F' });
  };

  const enrichedPositions = useMemo(() => {
    return positions.map(pos => {
      const pool = pools.find(p => String(p.id) === String(pos.poolId));
      const currentApr = pool ? pool.apr : 0;
      const days = daysBetween(pos.entryDate);
      const earnings = pos.amountInvested * (currentApr / 100) * (days / 365);
      const currentValue = pos.amountInvested + earnings;
      const pnlPercent = pos.amountInvested > 0 ? (earnings / pos.amountInvested) * 100 : 0;
      return { ...pos, currentApr, days, earnings, currentValue, pnlPercent, pool };
    });
  }, [positions, pools]);

  const sortedPositions = useMemo(() => {
    return [...enrichedPositions].sort((a, b) => {
      if (sortBy === 'earnings') return b.earnings - a.earnings;
      if (sortBy === 'invested') return b.amountInvested - a.amountInvested;
      if (sortBy === 'apr') return b.currentApr - a.currentApr;
      return 0;
    });
  }, [enrichedPositions, sortBy]);

  const summary = useMemo(() => {
    let totalInvested = 0;
    let totalEarnings = 0;
    let weightedApr = 0;

    if (enrichedPositions.length > 0) {
      totalInvested = enrichedPositions.reduce((s, p) => s + p.amountInvested, 0);
      totalEarnings = enrichedPositions.reduce((s, p) => s + p.earnings, 0);
      weightedApr = totalInvested > 0
        ? enrichedPositions.reduce((s, p) => s + p.currentApr * p.amountInvested, 0) / totalInvested
        : 0;
    }

    let walletValue = 0;
    if (includeWalletInTotals && enrichedWalletPositions.length > 0) {
      walletValue = walletSummary.totalValue;
    }

    const totalValue = totalInvested + totalEarnings + walletValue;
    const avgApr = weightedApr;

    return { totalInvested, totalValue, totalEarnings, avgApr, walletValue };
  }, [enrichedPositions, includeWalletInTotals, enrichedWalletPositions.length, walletSummary.totalValue]);

  const protocolAllocation = useMemo(() => {
    const map = {};
    enrichedPositions.forEach(p => {
      map[p.protocol] = (map[p.protocol] || 0) + p.amountInvested;
    });
    const total = Object.values(map).reduce((s, v) => s + v, 0);
    return Object.entries(map).map(([name, value]) => ({
      name,
      value,
      pct: total > 0 ? (value / total) * 100 : 0,
      color: PROTOCOL_COLORS[name] || '#999',
    }));
  }, [enrichedPositions]);

  const chainAllocation = useMemo(() => {
    const map = {};
    enrichedPositions.forEach(p => {
      map[p.chain] = (map[p.chain] || 0) + p.amountInvested;
    });
    const total = Object.values(map).reduce((s, v) => s + v, 0);
    return Object.entries(map).map(([name, value]) => ({
      name,
      value,
      pct: total > 0 ? (value / total) * 100 : 0,
      color: CHAIN_COLORS[name] || '#999',
    }));
  }, [enrichedPositions]);

  // Data for the yield comparison bar chart
  const yieldBarData = useMemo(() => {
    return enrichedPositions.map(pos => ({
      name: pos.poolName.length > 12 ? pos.poolName.slice(0, 12) + '...' : pos.poolName,
      yield: parseFloat(pos.earnings.toFixed(2)),
      apr: pos.currentApr,
      color: PROTOCOL_COLORS[pos.protocol] || '#999',
    }));
  }, [enrichedPositions]);

  // Combined pie chart data (by position value)
  const allocationPieData = useMemo(() => {
    const total = enrichedPositions.reduce((s, p) => s + p.currentValue, 0);
    return enrichedPositions.map(pos => ({
      name: pos.poolName,
      value: parseFloat(pos.currentValue.toFixed(2)),
      pct: total > 0 ? (pos.currentValue / total) * 100 : 0,
      color: PROTOCOL_COLORS[pos.protocol] || '#999',
    }));
  }, [enrichedPositions]);

  const sorts = [
    { key: 'earnings', label: 'EARNINGS' },
    { key: 'invested', label: 'INVESTED' },
    { key: 'apr', label: 'APR' },
  ];

  const isAddEnabled = selectedPool && amount && Number(amount) > 0;

  const hasAnything = enrichedPositions.length > 0 || enrichedWalletPositions.length > 0;

  return (
    <div style={{ padding: isMobile ? "12px" : "20px 24px" }}>
      <Tooltip id="metric-tooltip" style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 11,
        maxWidth: 240,
        borderRadius: 6,
        zIndex: 9999,
      }} />

      {/* ============================================================ */}
      {/* WALLET POSITIONS SECTION                                     */}
      {/* ============================================================ */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        style={{
          background: colors.surface,
          borderRadius: 12,
          padding: isMobile ? "16px" : "24px",
          boxShadow: `0 1px 4px ${colors.shadow}`,
          border: `1px solid ${colors.border}`,
          marginBottom: 24,
          transition: "background-color 0.3s ease, border-color 0.3s ease",
        }}
      >
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: isConnected ? 16 : 0,
          flexWrap: "wrap",
          gap: 8,
        }}>
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 10,
            color: colors.green,
            letterSpacing: 1.5,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}>
            <div style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: isConnected ? colors.green : colors.labelLight,
              animation: isConnected ? "pulse 2s infinite" : "none",
            }} />
            WALLET POSITIONS
          </div>

          {isConnected && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 10,
                color: colors.textSecondary,
                background: colors.greenBg,
                border: `1px solid ${colors.greenBorder}`,
                padding: "2px 8px",
                borderRadius: 3,
              }}>
                {formatAddress(account)}
              </span>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={fetchAllWalletPositions}
                disabled={walletLoading}
                style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 9,
                  padding: "4px 8px",
                  borderRadius: 4,
                  border: `1px solid ${colors.border}`,
                  background: "transparent",
                  color: colors.labelMuted,
                  cursor: walletLoading ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
                title="Refresh positions"
              >
                <HiOutlineRefresh size={12} style={{
                  animation: walletLoading ? "spin 1s linear infinite" : "none",
                }} />
                REFRESH
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={disconnect}
                style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 9,
                  padding: "4px 8px",
                  borderRadius: 4,
                  border: `1px solid ${colors.border}`,
                  background: "transparent",
                  color: colors.labelMuted,
                  cursor: "pointer",
                }}
              >
                DISCONNECT
              </motion.button>
            </div>
          )}
        </div>

        {/* Not connected — CTA */}
        {!isConnected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{ textAlign: "center", padding: "20px 0" }}
          >
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 11,
              color: colors.labelLight,
              marginBottom: 12,
            }}>
              Connect your wallet to see your on-chain DeFi positions
            </div>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={handleConnectWallet}
              disabled={isConnecting}
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 11,
                fontWeight: 600,
                padding: "10px 24px",
                borderRadius: 20,
                border: "none",
                background: colors.green,
                color: "#FFFFFF",
                cursor: isConnecting ? "wait" : "pointer",
                letterSpacing: 0.5,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <div style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: "#FFFFFF",
                opacity: 0.7,
              }} />
              {isConnecting ? 'CONNECTING...' : 'CONNECT WALLET'}
            </motion.button>
          </motion.div>
        )}

        {/* Connected — loading */}
        {isConnected && walletLoading && (
          <div>
            {walletProgress && (
              <div style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 10,
                color: colors.labelMuted,
                marginBottom: 12,
                letterSpacing: 0.5,
              }}>
                {walletProgress}
              </div>
            )}
            <div style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(280px, 1fr))",
              gap: 12,
            }}>
              {[1, 2, 3].map(i => <SkeletonCard key={i} colors={colors} />)}
            </div>
          </div>
        )}

        {/* Connected — error */}
        {isConnected && !walletLoading && walletError && (
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 11,
            color: colors.red || '#B8001A',
            background: colors.redBg || '#FFF0F2',
            border: `1px solid ${colors.redBorder || '#F0A0A8'}`,
            padding: "10px 14px",
            borderRadius: 8,
          }}>
            {walletError}
          </div>
        )}

        {/* Connected — wallet summary row */}
        {isConnected && !walletLoading && enrichedWalletPositions.length > 0 && (
          <>
            <div style={{
              display: "flex",
              gap: 12,
              marginBottom: 16,
              flexWrap: "wrap",
            }}>
              {[
                { label: "WALLET VALUE", value: walletSummary.totalValue, prefix: '$', accent: colors.green },
                { label: "UNCOLLECTED FEES", value: walletSummary.totalFees, prefix: '$', accent: colors.gold },
                { label: "POSITIONS", value: walletSummary.positionCount, prefix: '', accent: colors.green },
                { label: "CHAINS", value: walletSummary.chainsActive, prefix: '', accent: colors.green },
              ].map(card => (
                <div key={card.label} style={{
                  flex: "1 1 140px",
                  background: colors.greenBg,
                  borderRadius: 8,
                  padding: "12px 14px",
                  border: `1px solid ${colors.greenBorder}`,
                }}>
                  <div style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 8,
                    color: colors.labelLight,
                    letterSpacing: 1.2,
                    fontWeight: 600,
                    marginBottom: 4,
                  }}>
                    {card.label}
                  </div>
                  <div style={{
                    fontFamily: "'DM Serif Display', serif",
                    fontSize: 20,
                    color: card.accent,
                  }}>
                    <CountUp
                      end={card.value}
                      prefix={card.prefix}
                      decimals={card.prefix === '$' ? 2 : 0}
                      duration={1}
                      separator=","
                      preserveValue
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Wallet position cards */}
            <div style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(300px, 1fr))",
              gap: 12,
            }}>
              <AnimatePresence mode="popLayout">
                {enrichedWalletPositions.map((wp, index) => {
                  const chainColor = CHAIN_COLORS[wp.chain] || colors.textSecondary;
                  const matchedApr = wp.matchingPool ? wp.matchingPool.apr : null;

                  return (
                    <motion.div
                      key={`wallet-${wp.tokenId}-${wp.chain}`}
                      variants={cardVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      transition={{ duration: 0.3, delay: index * 0.04 }}
                      layout
                      style={{
                        background: colors.surface,
                        borderRadius: 10,
                        padding: "16px",
                        border: `1px solid ${colors.greenBorder}`,
                        borderLeft: `3px solid ${colors.green}`,
                        position: "relative",
                      }}
                    >
                      {/* Header */}
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                        <TokenStack tokens={[wp.token0.symbol, wp.token1.symbol]} size={24} />
                        <div style={{ flex: 1 }}>
                          <div style={{
                            fontFamily: "'DM Serif Display', serif",
                            fontSize: 14,
                            color: colors.textPrimary,
                          }}>
                            {wp.pairName}
                          </div>
                          <div style={{ display: "flex", gap: 4, marginTop: 3 }}>
                            <span style={{
                              fontFamily: "'IBM Plex Mono', monospace",
                              fontSize: 8,
                              color: "#E0006E",
                              background: "#E0006E12",
                              border: "1px solid #E0006E30",
                              padding: "1px 5px",
                              borderRadius: 3,
                              fontWeight: 500,
                            }}>
                              Uniswap V3
                            </span>
                            <span style={{
                              fontFamily: "'IBM Plex Mono', monospace",
                              fontSize: 8,
                              color: chainColor,
                              background: `${chainColor}12`,
                              border: `1px solid ${chainColor}30`,
                              padding: "1px 5px",
                              borderRadius: 3,
                              fontWeight: 500,
                            }}>
                              {wp.chain}
                            </span>
                            <span style={{
                              fontFamily: "'IBM Plex Mono', monospace",
                              fontSize: 8,
                              color: colors.labelMuted,
                              background: colors.hover,
                              padding: "1px 5px",
                              borderRadius: 3,
                            }}>
                              {fmtFeeTier(wp.feeTier)}
                            </span>
                          </div>
                        </div>
                        {/* In range / out of range badge */}
                        <span style={{
                          fontFamily: "'IBM Plex Mono', monospace",
                          fontSize: 8,
                          fontWeight: 600,
                          color: wp.inRange ? colors.green : (colors.red || '#B8001A'),
                          background: wp.inRange ? colors.greenBg : (colors.redBg || '#FFF0F2'),
                          border: `1px solid ${wp.inRange ? colors.greenBorder : (colors.redBorder || '#F0A0A8')}`,
                          padding: "2px 6px",
                          borderRadius: 3,
                          whiteSpace: "nowrap",
                        }}>
                          {wp.inRange ? 'IN RANGE' : 'OUT OF RANGE'}
                        </span>
                      </div>

                      {/* Metrics */}
                      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                        {[
                          { label: "VALUE", value: fmtUsd(wp.totalValueUsd) },
                          { label: "UNCOLLECTED FEES", value: fmtUsd(wp.feesUsd), color: colors.gold },
                          ...(matchedApr != null ? [{ label: "CURRENT APR", value: `${matchedApr.toFixed(2)}%`, color: matchedApr > 20 ? colors.gold : colors.green }] : []),
                          { label: `${wp.token0.symbol}`, value: wp.amount0.toFixed(wp.amount0 < 1 ? 6 : 2) },
                          { label: `${wp.token1.symbol}`, value: wp.amount1.toFixed(wp.amount1 < 1 ? 6 : 2) },
                        ].map(m => (
                          <div key={m.label} style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}>
                            <span style={{
                              fontFamily: "'IBM Plex Mono', monospace",
                              fontSize: 9,
                              color: colors.labelLight,
                              letterSpacing: 0.8,
                            }}>
                              {m.label}
                            </span>
                            <span style={{
                              fontFamily: "'IBM Plex Mono', monospace",
                              fontSize: 10,
                              color: m.color || colors.textSecondary,
                              fontWeight: 500,
                            }}>
                              {m.value}
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* Type badge */}
                      <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end" }}>
                        <span style={{
                          fontFamily: "'IBM Plex Mono', monospace",
                          fontSize: 8,
                          fontWeight: 600,
                          color: colors.green,
                          background: colors.greenBg,
                          border: `1px solid ${colors.greenBorder}`,
                          padding: "2px 6px",
                          borderRadius: 3,
                        }}>
                          DEPOSIT
                        </span>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          </>
        )}

        {/* Connected — no positions found */}
        {isConnected && !walletLoading && !walletError && enrichedWalletPositions.length === 0 && (
          <div style={{
            textAlign: "center",
            padding: "16px 0",
          }}>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 11,
              color: colors.labelLight,
            }}>
              No DeFi positions found. Your wallet may only have token balances.
            </div>
          </div>
        )}
      </motion.div>

      {/* ============================================================ */}
      {/* SEPARATOR                                                    */}
      {/* ============================================================ */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        marginBottom: 20,
      }}>
        <div style={{ flex: 1, height: 1, background: colors.border }} />
        <span style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 9,
          color: colors.labelLight,
          letterSpacing: 1.5,
          fontWeight: 600,
        }}>
          MANUAL TRACKING
        </span>
        <div style={{ flex: 1, height: 1, background: colors.border }} />
      </div>

      {/* Toggle Add Form Button */}
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => setShowForm(prev => !prev)}
        style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 11,
          fontWeight: 600,
          padding: "9px 20px",
          borderRadius: 8,
          border: `1px solid ${colors.border}`,
          background: colors.surface,
          color: colors.green,
          cursor: "pointer",
          letterSpacing: 0.5,
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginBottom: 16,
          transition: "background-color 0.3s ease, border-color 0.3s ease",
        }}
      >
        <HiOutlinePlusCircle size={16} />
        {showForm ? 'HIDE FORM' : 'ADD POSITION'}
      </motion.button>

      {/* Add Position Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0, marginBottom: 0 }}
            animate={{ opacity: 1, height: 'auto', marginBottom: 20 }}
            exit={{ opacity: 0, height: 0, marginBottom: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
          >
            <div style={{
              background: colors.surface,
              borderRadius: 12,
              padding: isMobile ? "16px" : "24px",
              boxShadow: `0 1px 4px ${colors.shadow}`,
              border: `1px solid ${colors.border}`,
              transition: "background-color 0.3s ease, border-color 0.3s ease",
            }}>
              <div style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 10,
                color: colors.green,
                letterSpacing: 1.5,
                fontWeight: 600,
                marginBottom: 16,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}>
                <HiOutlinePlusCircle size={14} />
                ADD POSITION
              </div>

              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", flexDirection: isMobile ? "column" : "row" }}>
                {/* Pool selector */}
                <div style={{ flex: "1 1 260px", position: "relative" }}>
                  <label style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 9,
                    color: colors.labelLight,
                    letterSpacing: 1,
                    display: "block",
                    marginBottom: 4,
                  }}>
                    POOL
                  </label>
                  <input
                    type="text"
                    placeholder="Search pools..."
                    value={selectedPool ? `${selectedPool.name} \u2014 ${selectedPool.protocol} \u00B7 ${selectedPool.chain}` : poolSearch}
                    onChange={e => {
                      setPoolSearch(e.target.value);
                      setSelectedPoolId('');
                      setShowDropdown(true);
                    }}
                    onFocus={() => {
                      if (!selectedPool) setShowDropdown(true);
                    }}
                    onClick={() => {
                      if (selectedPool) {
                        setSelectedPoolId('');
                        setPoolSearch('');
                        setShowDropdown(true);
                      }
                    }}
                    style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 12,
                      padding: "8px 14px",
                      borderRadius: 8,
                      border: `1px solid ${colors.border}`,
                      background: colors.inputBg,
                      color: colors.textPrimary,
                      width: "100%",
                      outline: "none",
                      boxSizing: "border-box",
                      transition: "background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease",
                    }}
                  />
                  {showDropdown && !selectedPool && filteredPools.length > 0 && (
                    <div style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      background: colors.surface,
                      border: `1px solid ${colors.border}`,
                      borderRadius: 8,
                      boxShadow: `0 4px 12px ${colors.shadowHover}`,
                      zIndex: 100,
                      maxHeight: 240,
                      overflowY: "auto",
                    }}>
                      {filteredPools.map(p => (
                        <div
                          key={p.id}
                          onClick={() => {
                            setSelectedPoolId(String(p.id));
                            setPoolSearch('');
                            setShowDropdown(false);
                          }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "8px 12px",
                            cursor: "pointer",
                            borderBottom: `1px solid ${colors.borderSubtle}`,
                            transition: "background 0.1s",
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = colors.hover}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <TokenStack tokens={p.tokens} size={20} />
                          <span style={{
                            fontFamily: "'DM Serif Display', serif",
                            fontSize: 13,
                            color: colors.textPrimary,
                          }}>
                            {p.name}
                          </span>
                          <span style={{
                            fontFamily: "'IBM Plex Mono', monospace",
                            fontSize: 9,
                            color: colors.labelMuted,
                          }}>
                            {p.protocol} · {p.chain}
                          </span>
                          <span style={{
                            fontFamily: "'DM Serif Display', serif",
                            fontSize: 12,
                            color: p.apr > 20 ? colors.gold : colors.green,
                            marginLeft: "auto",
                          }}>
                            {p.apr.toFixed(2)}%
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Amount */}
                <div style={{ flex: isMobile ? "none" : "0 0 160px", width: isMobile ? "100%" : "auto" }}>
                  <label style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 9,
                    color: colors.labelLight,
                    letterSpacing: 1,
                    display: "block",
                    marginBottom: 4,
                  }}>
                    AMOUNT (USD)
                  </label>
                  <input
                    type="number"
                    placeholder="0.00"
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    min="0"
                    step="0.01"
                    style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 12,
                      padding: "8px 14px",
                      borderRadius: 8,
                      border: `1px solid ${colors.border}`,
                      background: colors.inputBg,
                      color: colors.textPrimary,
                      width: "100%",
                      outline: "none",
                      boxSizing: "border-box",
                      transition: "background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease",
                    }}
                  />
                </div>

                {/* Entry Date */}
                <div style={{ flex: isMobile ? "none" : "0 0 160px", width: isMobile ? "100%" : "auto" }}>
                  <label style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 9,
                    color: colors.labelLight,
                    letterSpacing: 1,
                    display: "block",
                    marginBottom: 4,
                  }}>
                    ENTRY DATE
                  </label>
                  <input
                    type="date"
                    value={entryDate}
                    onChange={e => setEntryDate(e.target.value)}
                    style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 12,
                      padding: "8px 14px",
                      borderRadius: 8,
                      border: `1px solid ${colors.border}`,
                      background: colors.inputBg,
                      color: colors.textPrimary,
                      width: "100%",
                      outline: "none",
                      boxSizing: "border-box",
                      transition: "background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease",
                    }}
                  />
                </div>

                {/* Add button */}
                <motion.button
                  whileHover={isAddEnabled ? { scale: 1.04 } : {}}
                  whileTap={isAddEnabled ? { scale: 0.96 } : {}}
                  onClick={handleAdd}
                  disabled={!isAddEnabled}
                  style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 11,
                    fontWeight: 600,
                    padding: "9px 20px",
                    borderRadius: 8,
                    border: "none",
                    background: isAddEnabled ? colors.green : colors.border,
                    color: isAddEnabled ? "#FFFFFF" : colors.labelLight,
                    cursor: isAddEnabled ? "pointer" : "not-allowed",
                    letterSpacing: 0.5,
                    transition: "all 0.15s",
                    whiteSpace: "nowrap",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <HiOutlinePlusCircle size={14} />
                  Add Position
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Summary Cards */}
      {hasAnything && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          style={{ marginBottom: 20 }}
        >
          {/* Include wallet toggle */}
          {enrichedWalletPositions.length > 0 && enrichedPositions.length > 0 && (
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 12,
            }}>
              <button
                onClick={() => setIncludeWalletInTotals(prev => !prev)}
                style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 9,
                  padding: "3px 10px",
                  borderRadius: 4,
                  border: `1px solid ${includeWalletInTotals ? colors.greenBorder : colors.border}`,
                  background: includeWalletInTotals ? colors.greenBg : "transparent",
                  color: includeWalletInTotals ? colors.green : colors.labelMuted,
                  cursor: "pointer",
                  fontWeight: 500,
                  letterSpacing: 0.5,
                }}
              >
                {includeWalletInTotals ? 'WALLET INCLUDED IN TOTALS' : 'WALLET EXCLUDED FROM TOTALS'}
              </button>
            </div>
          )}

          <div style={{
            display: "flex",
            gap: 16,
            flexWrap: "wrap",
          }}>
            {[
              { label: "TOTAL INVESTED", rawValue: summary.totalInvested, prefix: '$', decimals: 2, accent: colors.green },
              { label: "CURRENT VALUE", rawValue: summary.totalValue, prefix: '$', decimals: 2, accent: colors.green },
              { label: "EST. EARNINGS", rawValue: summary.totalEarnings, prefix: '+$', decimals: 2, accent: colors.gold },
              { label: "AVG PORTFOLIO APR", rawValue: summary.avgApr, suffix: '%', decimals: 2, accent: colors.gold },
            ].map(card => (
              <div key={card.label} style={{
                flex: "1 1 200px",
                background: colors.surface,
                borderRadius: 12,
                padding: "18px 20px",
                boxShadow: `0 1px 4px ${colors.shadow}`,
                border: `1px solid ${colors.border}`,
                borderTop: `3px solid ${card.accent}`,
                transition: "background-color 0.3s ease, border-color 0.3s ease",
              }}>
                <div style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 9,
                  color: colors.labelLight,
                  letterSpacing: 1.5,
                  fontWeight: 600,
                  marginBottom: 8,
                }}>
                  {card.label}
                </div>
                <div style={{
                  fontFamily: "'DM Serif Display', serif",
                  fontSize: 26,
                  color: card.accent,
                }}>
                  <CountUp
                    end={card.rawValue}
                    prefix={card.prefix || ''}
                    suffix={card.suffix || ''}
                    decimals={card.decimals}
                    duration={1.2}
                    separator=","
                    preserveValue
                  />
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Charts Section: PieChart + BarChart */}
      {enrichedPositions.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          style={{
            display: "flex",
            gap: 16,
            flexWrap: "wrap",
            marginBottom: 24,
          }}
        >
          {/* Portfolio Allocation PieChart */}
          <div style={{
            flex: "1 1 340px",
            background: colors.surface,
            borderRadius: 12,
            padding: "20px",
            boxShadow: `0 1px 4px ${colors.shadow}`,
            border: `1px solid ${colors.border}`,
            transition: "background-color 0.3s ease, border-color 0.3s ease",
          }}>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 9,
              color: colors.labelLight,
              letterSpacing: 1.5,
              fontWeight: 600,
              marginBottom: 14,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}>
              <HiOutlineChartPie size={14} />
              PORTFOLIO ALLOCATION
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={allocationPieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {allocationPieData.map((entry, index) => (
                    <Cell key={`pie-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <RechartsTooltip
                  formatter={(value, name) => [fmtUsd(value), name]}
                  contentStyle={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 11,
                    borderRadius: 6,
                    border: `1px solid ${colors.border}`,
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 8 }}>
              {allocationPieData.map(seg => (
                <div key={seg.name} style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{
                      width: 8,
                      height: 8,
                      borderRadius: 2,
                      background: seg.color,
                    }} />
                    <span style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 10,
                      color: colors.textSecondary,
                      fontWeight: 500,
                    }}>
                      {seg.name}
                    </span>
                  </div>
                  <span style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 10,
                    color: colors.labelMuted,
                  }}>
                    {seg.pct.toFixed(1)}%
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Yield Comparison BarChart */}
          <div style={{
            flex: "1 1 340px",
            background: colors.surface,
            borderRadius: 12,
            padding: "20px",
            boxShadow: `0 1px 4px ${colors.shadow}`,
            border: `1px solid ${colors.border}`,
            transition: "background-color 0.3s ease, border-color 0.3s ease",
          }}>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 9,
              color: colors.labelLight,
              letterSpacing: 1.5,
              fontWeight: 600,
              marginBottom: 14,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}>
              <HiOutlineChartPie size={14} />
              YIELD COMPARISON
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={yieldBarData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <XAxis
                  dataKey="name"
                  tick={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, fill: colors.labelMuted }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, fill: colors.labelMuted }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={v => `$${v}`}
                />
                <RechartsTooltip
                  formatter={(value) => [fmtUsd(value), 'Yield']}
                  contentStyle={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 11,
                    borderRadius: 6,
                    border: `1px solid ${colors.border}`,
                  }}
                />
                <Bar dataKey="yield" radius={[4, 4, 0, 0]}>
                  {yieldBarData.map((entry, index) => (
                    <Cell key={`bar-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      )}

      {/* Positions List */}
      {enrichedPositions.length > 0 && (
        <>
          {/* Sort bar */}
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 16,
          }}>
            <span style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 10,
              color: colors.labelLight,
              letterSpacing: 1,
            }}>
              SORT BY
            </span>
            {sorts.map(s => (
              <button key={s.key} onClick={() => setSortBy(s.key)} style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 10,
                padding: "4px 10px",
                borderRadius: 4,
                border: `1px solid ${sortBy === s.key ? colors.activeBg : colors.border}`,
                background: sortBy === s.key ? colors.activeBg : "transparent",
                color: sortBy === s.key ? colors.activeText : colors.labelMuted,
                cursor: "pointer",
                fontWeight: 500,
              }}>
                {s.label}
              </button>
            ))}
            <div style={{ flex: 1 }} />
            <span style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 10,
              color: colors.labelMuted,
            }}>
              {enrichedPositions.length} position{enrichedPositions.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Position cards */}
          <div style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(320px, 1fr))",
            gap: 16,
            marginBottom: 24,
          }}>
            <AnimatePresence mode="popLayout">
              {sortedPositions.map((pos, index) => {
                const protocolColor = PROTOCOL_COLORS[pos.protocol] || colors.textPrimary;
                const chainColor = CHAIN_COLORS[pos.chain] || colors.textSecondary;

                return (
                  <motion.div
                    key={pos.id}
                    variants={cardVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    layout
                    style={{
                      background: colors.surface,
                      borderRadius: 12,
                      padding: "20px",
                      boxShadow: `0 1px 4px ${colors.shadow}`,
                      border: `1px solid ${colors.border}`,
                      position: "relative",
                      transition: "background-color 0.3s ease, border-color 0.3s ease",
                    }}
                  >
                    {/* Delete button */}
                    <motion.button
                      whileHover={{ scale: 1.15 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleDelete(pos.id)}
                      style={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        width: 22,
                        height: 22,
                        borderRadius: 4,
                        border: `1px solid ${colors.border}`,
                        background: "transparent",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: colors.labelClose,
                        transition: "all 0.15s",
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.color = colors.red;
                        e.currentTarget.style.borderColor = colors.redBorder;
                        e.currentTarget.style.background = colors.redBg;
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.color = colors.labelClose;
                        e.currentTarget.style.borderColor = colors.border;
                        e.currentTarget.style.background = 'transparent';
                      }}
                      title="Remove position"
                    >
                      <HiOutlineTrash size={13} />
                    </motion.button>

                    {/* Header */}
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                      <TokenStack tokens={pos.tokens} size={28} />
                      <div>
                        <div style={{
                          fontFamily: "'DM Serif Display', serif",
                          fontSize: 16,
                          color: colors.textPrimary,
                        }}>
                          {pos.poolName}
                        </div>
                        <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
                          <span style={{
                            fontFamily: "'IBM Plex Mono', monospace",
                            fontSize: 9,
                            color: protocolColor,
                            background: `${protocolColor}12`,
                            border: `1px solid ${protocolColor}30`,
                            padding: "1px 6px",
                            borderRadius: 3,
                            fontWeight: 500,
                          }}>
                            {pos.protocol}
                          </span>
                          <span style={{
                            fontFamily: "'IBM Plex Mono', monospace",
                            fontSize: 9,
                            color: chainColor,
                            background: `${chainColor}12`,
                            border: `1px solid ${chainColor}30`,
                            padding: "1px 6px",
                            borderRadius: 3,
                            fontWeight: 500,
                          }}>
                            {pos.chain}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Metrics */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {[
                        { label: "INVESTED", value: fmtUsd(pos.amountInvested) },
                        { label: "CURRENT APR", value: `${(pos.currentApr || 0).toFixed(2)}%`, color: pos.currentApr > 20 ? colors.gold : colors.green },
                        { label: "EST. EARNINGS", value: `+${fmtUsd(pos.earnings)}`, color: colors.green },
                        { label: "EST. VALUE", value: fmtUsd(pos.currentValue) },
                        { label: "DAYS HELD", value: `${pos.days}d` },
                      ].map(m => (
                        <div key={m.label} style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}>
                          <span
                            data-tooltip-id="metric-tooltip"
                            data-tooltip-content={METRIC_TOOLTIPS[m.label] || ''}
                            style={{
                              fontFamily: "'IBM Plex Mono', monospace",
                              fontSize: 10,
                              color: colors.labelLight,
                              letterSpacing: 1,
                              cursor: METRIC_TOOLTIPS[m.label] ? 'help' : 'default',
                              borderBottom: METRIC_TOOLTIPS[m.label] ? `1px dotted ${colors.labelLight}` : 'none',
                            }}
                          >
                            {m.label}
                          </span>
                          <span style={{
                            fontFamily: "'IBM Plex Mono', monospace",
                            fontSize: 11,
                            color: m.color || colors.textSecondary,
                            fontWeight: 500,
                          }}>
                            {m.value}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* P&L indicator */}
                    <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}>
                      <span style={{
                        fontFamily: "'IBM Plex Mono', monospace",
                        fontSize: 10,
                        fontWeight: 600,
                        color: colors.green,
                        background: colors.greenBg,
                        border: `1px solid ${colors.greenBorder}`,
                        padding: "2px 8px",
                        borderRadius: 3,
                      }}>
                        +{pos.pnlPercent.toFixed(2)}% P&L
                      </span>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        </>
      )}

      {/* Protocol & Chain Allocation */}
      {enrichedPositions.length > 0 && (
        <div style={{
          display: "flex",
          gap: 16,
          flexWrap: "wrap",
          marginBottom: 24,
        }}>
          {/* Protocol allocation */}
          <div style={{
            flex: "1 1 300px",
            background: colors.surface,
            borderRadius: 12,
            padding: "20px",
            boxShadow: `0 1px 4px ${colors.shadow}`,
            border: `1px solid ${colors.border}`,
            transition: "background-color 0.3s ease, border-color 0.3s ease",
          }}>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 9,
              color: colors.labelLight,
              letterSpacing: 1.5,
              fontWeight: 600,
              marginBottom: 14,
            }}>
              ALLOCATION BY PROTOCOL
            </div>

            <div style={{
              display: "flex",
              borderRadius: 6,
              overflow: "hidden",
              height: 12,
              marginBottom: 14,
            }}>
              {protocolAllocation.map(seg => (
                <div key={seg.name} style={{
                  width: `${seg.pct}%`,
                  background: seg.color,
                  transition: "width 0.3s ease",
                  minWidth: seg.pct > 0 ? 4 : 0,
                }} />
              ))}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {protocolAllocation.map(seg => (
                <div key={seg.name} style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{
                      width: 8,
                      height: 8,
                      borderRadius: 2,
                      background: seg.color,
                    }} />
                    <span style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 11,
                      color: colors.textSecondary,
                      fontWeight: 500,
                    }}>
                      {seg.name}
                    </span>
                  </div>
                  <span style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 11,
                    color: colors.labelMuted,
                  }}>
                    {seg.pct.toFixed(1)}%
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Chain allocation */}
          <div style={{
            flex: "1 1 300px",
            background: colors.surface,
            borderRadius: 12,
            padding: "20px",
            boxShadow: `0 1px 4px ${colors.shadow}`,
            border: `1px solid ${colors.border}`,
            transition: "background-color 0.3s ease, border-color 0.3s ease",
          }}>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 9,
              color: colors.labelLight,
              letterSpacing: 1.5,
              fontWeight: 600,
              marginBottom: 14,
            }}>
              ALLOCATION BY CHAIN
            </div>

            <div style={{
              display: "flex",
              borderRadius: 6,
              overflow: "hidden",
              height: 12,
              marginBottom: 14,
            }}>
              {chainAllocation.map(seg => (
                <div key={seg.name} style={{
                  width: `${seg.pct}%`,
                  background: seg.color,
                  transition: "width 0.3s ease",
                  minWidth: seg.pct > 0 ? 4 : 0,
                }} />
              ))}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {chainAllocation.map(seg => (
                <div key={seg.name} style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{
                      width: 8,
                      height: 8,
                      borderRadius: 2,
                      background: seg.color,
                    }} />
                    <span style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 11,
                      color: colors.textSecondary,
                      fontWeight: 500,
                    }}>
                      {seg.name}
                    </span>
                  </div>
                  <span style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 11,
                    color: colors.labelMuted,
                  }}>
                    {seg.pct.toFixed(1)}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Empty state — only when no wallet positions AND no manual positions */}
      {enrichedPositions.length === 0 && enrichedWalletPositions.length === 0 && !walletLoading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          style={{
            textAlign: "center",
            padding: "60px 0",
          }}
        >
          <div style={{
            fontFamily: "'DM Serif Display', serif",
            fontSize: 24,
            color: colors.labelLighter,
            marginBottom: 8,
          }}>
            No positions yet
          </div>
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 12,
            color: colors.labelLight,
          }}>
            Connect your wallet or add a position manually to start tracking your DeFi portfolio.
          </div>
        </motion.div>
      )}

      {/* Spin animation for the refresh icon */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
